import clsx from "clsx";
import {
  ArrowDownAz,
  Calendar,
  EllipsisVertical,
  ListOrdered,
  LucideIcon,
  Merge,
  Pen,
  Plus,
  RefreshCw,
  Trash2,
} from "lucide-react";
import { FC, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";

import { OrganizationRole } from "~/api/model";
import { DisplayFor } from "~/components/permissions/DisplayFor";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { trackEvent } from "~/utils/analytics";
import { CategoryItem } from "../../types";

interface CategoryActionsProps {
  className?: string;
  handleOpenChange: (isOpen: boolean) => void;
  handleRename: () => void;
  handleDelete: () => void;
  handleMergeAll: () => void;
  categories: CategoryItem[];
  handleSortByName: () => void;
  handleSortByDate: () => void;
  handleSortByPageCount: () => void;
  canBeSorted: boolean;
  /**
   * Handler for the admin-only "New Category" action that runs the
   * currently-grouped pages through the `define-category` prompt and
   * persists the resulting org-level category definition. When undefined,
   * the item is hidden entirely.
   */
  handleDefineCategory?: () => void;
  isDefiningCategory?: boolean;
  /**
   * Handler for the admin-only "Redefine Category" action that refines
   * the existing org-level definition based on the current pages. Only
   * shown when the category is already linked to a DocumentCategory with
   * a non-empty definition.
   */
  handleRedefineCategory?: () => void;
  isRedefiningCategory?: boolean;
}

interface ActionItem {
  id: string;
  icon: LucideIcon;
  messageId: string;
  defaultMessage: string;
  className?: string;
  action: () => void;
  disabled?: boolean;
}

const ADMIN_ROLES = [OrganizationRole.SUPERADMIN, OrganizationRole.OWNER];

export const CategoryActions: FC<CategoryActionsProps> = ({
  className = "",
  handleOpenChange,
  handleRename,
  handleDelete,
  handleMergeAll,
  categories,
  handleSortByName,
  handleSortByDate,
  handleSortByPageCount,
  canBeSorted,
  handleDefineCategory,
  isDefiningCategory = false,
  handleRedefineCategory,
  isRedefiningCategory = false,
}) => {
  const [open, setOpen] = useState(false);

  const topItems: ActionItem[] = [
    {
      id: "editName",
      icon: Pen,
      messageId: "docList.category.actionsDropdown.rename",
      defaultMessage: "Rename",
      action: handleRename,
    },
    canBeSorted && {
      id: "sortByName",
      icon: ArrowDownAz,
      messageId: "docList.category.actionsDropdown.sortByName",
      defaultMessage: "Sort by Name",
      action: handleSortByName,
    },
    canBeSorted && {
      id: "sortByDate",
      icon: Calendar,
      messageId: "docList.category.actionsDropdown.sortByDate",
      defaultMessage: "Sort by Date",
      action: handleSortByDate,
    },
    canBeSorted && {
      id: "sortByPageCount",
      icon: ListOrdered,
      messageId: "docList.category.actionsDropdown.sortByPageCount",
      defaultMessage: "Sort by Page Count",
      action: handleSortByPageCount,
    },
    canBeSorted && {
      id: "mergeAll",
      icon: Merge,
      messageId: "docList.category.actionsDropdown.mergeAll",
      defaultMessage: "Merge All",
      action: handleMergeAll,
    },
  ].filter(Boolean) as ActionItem[];

  const deleteItem: ActionItem | null =
    categories.length > 1
      ? {
          id: "delete",
          icon: Trash2,
          messageId: "docList.category.actionsDropdown.delete",
          defaultMessage: "Delete",
          action: handleDelete,
        }
      : null;

  useEffect(() => {
    if (open) {
      trackEvent({ eventName: "app/doc_list/category_actions.button/click" });
    }
  }, [open]);

  const renderItem = (item: ActionItem) => (
    <DropdownMenuItem
      key={item.id}
      className={clsx("cursor-pointer whitespace-nowrap", item.className)}
      onClick={item.action}
      disabled={item.disabled}
      destructive={item.id === "delete"}
    >
      <item.icon className="mr-2 h-4 w-4" />
      <FormattedMessage
        id={item.messageId}
        defaultMessage={item.defaultMessage}
      />
    </DropdownMenuItem>
  );

  const defineCategoryItem: ActionItem | null = handleDefineCategory
    ? {
        id: "defineCategory",
        icon: Plus,
        messageId: "docList.category.actionsDropdown.defineCategory",
        defaultMessage: "Add Category",
        action: () => {
          trackEvent({
            eventName:
              "app/doc_list/category_actions_menu/define_category.link/click",
          });
          handleDefineCategory();
        },
        disabled: isDefiningCategory,
      }
    : null;

  const redefineCategoryItem: ActionItem | null = handleRedefineCategory
    ? {
        id: "redefineCategory",
        icon: RefreshCw,
        messageId: "docList.category.actionsDropdown.redefineCategory",
        defaultMessage: "Refine Category",
        action: () => {
          trackEvent({
            eventName:
              "app/doc_list/category_actions_menu/redefine_category.link/click",
          });
          handleRedefineCategory();
        },
        disabled: isRedefiningCategory,
      }
    : null;

  return (
    <div onClick={(e) => e.stopPropagation()} className="ml-auto mr-6">
      <DropdownMenu
        open={open}
        onOpenChange={(isOpen) => {
          setOpen(isOpen);
          handleOpenChange(isOpen);
        }}
      >
        <DropdownMenuTrigger asChild>
          <div
            className={clsx(
              "p-1 rounded-sm cursor-pointer hover:bg-white/10",
              className
            )}
          >
            <EllipsisVertical className="w-4 h-4 text-white" />
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-34" align="end">
          {topItems.map(renderItem)}
          {defineCategoryItem && (
            <DisplayFor roles={ADMIN_ROLES}>
              {renderItem(defineCategoryItem)}
            </DisplayFor>
          )}
          {redefineCategoryItem && (
            <DisplayFor roles={ADMIN_ROLES}>
              {renderItem(redefineCategoryItem)}
            </DisplayFor>
          )}
          {deleteItem && renderItem(deleteItem)}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
